import { useEffect, useState } from "react";
import axiosInstance from "../../../hooks/axiosInstance";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { Oval } from "react-loader-spinner";
import WarehouseModal from "../../../components/Modal/Admin/InventoryManagement/WarehouseModal";

export default function ManageWarehouse() {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [warehouseToEdit, setWarehouseToEdit] = useState(null);
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  const MySwal = withReactContent(Swal);

  useEffect(() => {
    const fetchWarehouses = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get("/user/warehouses/fetch", {
          params: { page, filter },
        });
        setWarehouses(response.data.data.warehouses.data);
        setPagination(response.data.data.warehouses);
      } catch (error) {
        console.error("Error fetching warehouses:", error);
        toast.error("Failed to fetch warehouses");
      } finally {
        setLoading(false);
      }
    };
    fetchWarehouses();
  }, [page, filter, refreshKey]);

  const handleCreate = () => {
    setWarehouseToEdit(null);
    setShowModal(true);
  };

  const handleEdit = (warehouse) => {
    setWarehouseToEdit(warehouse);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#c4281b",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axiosInstance.delete(`/user/warehouses/delete/${id}`);
          toast.success("Warehouse deleted successfully");
          setRefreshKey((prev) => prev + 1);
        } catch (error) {
          toast.error(error.response?.data?.message || "Failed to delete warehouse");
        }
      }
    });
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <h4 className="mb-sm-0">Warehouse Management</h4>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-header border-0">
                <div className="row g-4">
                  <div className="col-sm-auto">
                    <div>
                      <button className="btn btn-success add-btn" onClick={handleCreate}>
                        <i className="ri-add-line align-bottom me-1"></i> Add Warehouse
                      </button>
                    </div>
                  </div>
                  <div className="col-sm">
                    <div className="d-flex justify-content-sm-end">
                      <div className="search-box ms-2">
                        <input
                          type="text"
                          className="form-control search"
                          placeholder="Search..."
                          value={filter}
                          onChange={(e) => setFilter(e.target.value)}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-body">
                <div className="table-responsive table-card">
                  <table className="table align-middle table-nowrap">
                    <thead className="table-light">
                      <tr>
                        <th scope="col">Name</th>
                        <th scope="col">Address</th>
                        <th scope="col">Status</th>
                        <th scope="col">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="4" className="text-center">
                            <Oval height={40} width={40} color="#c4281b" />
                          </td>
                        </tr>
                      ) : warehouses && warehouses.length > 0 ? (
                        warehouses.map((warehouse) => (
                          <tr key={warehouse.id}>
                            <td>{warehouse.name}</td>
                            <td>{warehouse.address || "N/A"}</td>
                            <td>
                              <span className={`badge bg-${warehouse.status === "active" ? "success" : "danger"}-subtle text-${warehouse.status === "active" ? "success" : "danger"} text-uppercase`}>
                                {warehouse.status}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                <button className="btn btn-sm btn-info edit-item-btn" onClick={() => handleEdit(warehouse)}>
                                  Edit
                                </button>
                                <button className="btn btn-sm btn-danger remove-item-btn" onClick={() => handleDelete(warehouse.id)}>
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center">No Warehouses Found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex justify-content-end mt-3">
                  <nav>
                    <ul className="pagination">
                      <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => setPage(page - 1)}>Previous</button>
                      </li>
                      {[...Array(pagination.last_page || 0)].map((_, i) => (
                        <li key={i} className={`page-item ${page === i + 1 ? "active" : ""}`}>
                          <button className="page-link" onClick={() => setPage(i + 1)}>{i + 1}</button>
                        </li>
                      ))}
                      <li className={`page-item ${page === pagination.last_page ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => setPage(page + 1)}>Next</button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <WarehouseModal
        show={showModal}
        closeModal={() => setShowModal(false)}
        refreshPage={() => setRefreshKey((prev) => prev + 1)}
        warehouseToEdit={warehouseToEdit}
      />
    </div>
  );
}
